import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import settingsIconSelected from '../assets/settings_selected.png';
import settingsIconInactive from '../assets/settings_unselected.png';
import settingsIconHalf from '../assets/settings 3.png';
import activityIconSelected from '../assets/activity_icon__selected_.png';
import activityIconNotSelected from '../assets/activity_icon__not_selected_.png';
import TermsContent from '../components/TermsContent';
import PrivacyContent from '../components/PrivacyContent';
import EmailNotificationToggle from '../components/EmailNotificationToggle';
import SmsPhoneVerification from '../components/SmsPhoneVerification';
import InvestorActivityPanel from '../components/settings/InvestorActivityPanel';
import InvestorProfileComponent from '../components/profile/InvestorProfile';
import ProfileWizard, { ProfileWizardLeaveGuard } from '../components/profile/ProfileWizard';
import StartupRow from '../components/StartupRow';
import { CATALOG_CATEGORY_LABELS } from '../constants/catalog-categories';
import { StartupProfile } from '../types/firestore';
import { startupNameSlug } from '../utils/startupNameSlug';
import { rememberOpenStartupProfile } from '../utils/openStartupProfile';
import { PRAIRI_SURFACES } from '../constants/prairiSurfaces';
import { SUCCESS_FEE_LIVE } from '../constants/successFee';
import PauseProfilePage from './PauseProfilePage';

/** Page fill — same as Favorites when a profile is open. */
const PAGE_BG = PRAIRI_SURFACES.page;

/** Left nav card — same as the compact profile card. */
const SETTINGS_NAV_SURFACE = PRAIRI_SURFACES.profileCard;

/** Right pane — same as the Favorites / dashboard detail pane. */
const SETTINGS_SURFACE = PRAIRI_SURFACES.page;

/** Selected Activity / Settings card outline — same grey as Specially-for-you boxes. */
const SETTINGS_CARD_BORDER = '#555555';

/** Fixed grey thumb. Native thumbs shrink to a sliver on long pages. */
const SETTINGS_THUMB_PX = 260;

function SettingsScroll({
  className,
  scrollClassName,
  children,
  contentKey,
  legal,
}: {
  className?: string;
  scrollClassName?: string;
  children: React.ReactNode;
  contentKey?: string | number;
  legal?: boolean;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    const thumbEl = thumbRef.current;
    if (!el || !thumbEl) return;

    const update = () => {
      const view = el.clientHeight;
      const range = el.scrollHeight - view;
      const canScroll = range > 1 && view > 0;
      thumbEl.style.opacity = canScroll ? '1' : '0';
      const height = Math.min(SETTINGS_THUMB_PX, Math.max(48, view - 16));
      const maxTop = Math.max(0, view - height);
      const top = range <= 0 ? 0 : (el.scrollTop / range) * maxTop;
      thumbEl.style.top = `${top}px`;
      thumbEl.style.height = `${height}px`;
    };

    update();
    const later = window.setTimeout(update, 80);
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => {
      window.clearTimeout(later);
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [contentKey]);

  return (
    <div className={`relative min-h-0 ${className || ''}`}>
      <div
        ref={scrollRef}
        data-settings-legal-scroll={legal ? '' : undefined}
        className={`absolute inset-0 overflow-y-auto overflow-x-hidden prairi-hide-native-scrollbar ${scrollClassName || ''}`}
      >
        {children}
      </div>
      <div className="pointer-events-none absolute inset-y-0 right-1 z-10 w-2" aria-hidden>
        <div
          ref={thumbRef}
          className="absolute right-0 w-1.5 rounded-full bg-[#8a8a8a]"
          style={{ top: 0, height: SETTINGS_THUMB_PX }}
        />
      </div>
    </div>
  );
}

/** Cap at the viewport. Do not force this height — a short menu would leave a void above Specially for you. */
const SETTINGS_CARD_HEIGHT = 'max-h-[calc(100vh-3.5rem)]';

// ─── Notification Panel ────────────────────────────────────────────────────────
const NotificationsPanel: React.FC = () => {
  return (
    <div
      className="p-6 md:p-8 max-w-lg flex-1 min-h-0 overflow-y-auto text-left prairi-hide-native-scrollbar"
      style={{ backgroundColor: SETTINGS_SURFACE }}
    >
      <h2 className="text-white text-xl font-bold mb-6">Notification Settings</h2>
      <EmailNotificationToggle />
      <SmsPhoneVerification />
    </div>
  );
};

// ─── Password Panel ─────────────────────────────────────────────────────────────
const PasswordPanel: React.FC = () => {
  const fns = getFunctions();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'sent' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setMessage('');
    try {
      await httpsCallable(fns, 'sendPasswordReset')({ email });
      setStatus('sent');
    } catch (err: unknown) {
      setStatus('error');
      setMessage((err as Error)?.message || 'Failed to send reset email.');
    }
  };

  return (
    <div
      className="p-6 md:p-8 max-w-lg flex-1 min-h-0 overflow-y-auto text-left prairi-hide-native-scrollbar"
      style={{ backgroundColor: SETTINGS_SURFACE }}
    >
      <h2 className="text-white text-xl font-bold mb-2">Change your password</h2>
      <p className="text-gray-400 text-sm mb-6">
        Enter your email address and we&apos;ll send you a secure link to reset your password.
      </p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <label className="block text-gray-300 text-sm mb-1" htmlFor="reset-email">
            Email address
          </label>
          <input
            id="reset-email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            placeholder="you@example.com"
            disabled={status === 'loading'}
            className="w-full bg-gray-700 border border-gray-600 text-white rounded px-3 py-2 focus:outline-none focus:border-emerald-500 disabled:opacity-50"
          />
        </div>

        <button
          type="submit"
          disabled={status === 'loading'}
          className="border-2 border-emerald-500 text-white px-6 py-2 rounded-xl disabled:opacity-50 font-bold w-fit"
        >
          {status === 'loading' ? 'Sending…' : 'Send reset link'}
        </button>
      </form>

      {status === 'sent' && (
        <p className="text-green-400 mt-4 text-sm">
          ✅ If an account exists for {email}, you will receive a password reset email shortly.
        </p>
      )}
      {status === 'error' && <p className="text-red-400 mt-4 text-sm">❌ {message}</p>}
    </div>
  );
};

// ─── Legal document panels (Terms / Privacy) ────────────────────────────────────
const LegalDocumentPanel: React.FC<{
  title: string;
  revisionLine: string;
  proseToneClassName: string;
  children: React.ReactNode;
  onClose: () => void;
}> = ({ title, revisionLine, proseToneClassName, children, onClose }) => (
  <div
    className="flex h-full min-h-0 flex-1 flex-col w-full text-left"
    style={{ backgroundColor: SETTINGS_SURFACE }}
  >
    <header className="shrink-0 border-b border-gray-600/70 pl-6 pr-3 pt-6 pb-0 md:pl-8 md:pr-4 md:pt-8">
      <h2 id="settings-legal-dialog-title" className="text-white font-bold text-2xl">
        {title}
      </h2>
      <p className="text-gray-400 text-sm italic mt-2 pb-4">{revisionLine}</p>
    </header>

    <SettingsScroll className="flex-1 min-h-0 basis-0 min-w-0" contentKey={title} legal>
      {/* Right padding only clears the scrollbar pill so the text reaches the card edge. */}
      <div className={`min-h-0 w-full pl-6 pr-2 md:pl-8 py-6 ${proseToneClassName}`}>
        {children}
      </div>
    </SettingsScroll>

    <footer className="shrink-0 border-t border-gray-600/70 pl-6 pr-3 md:pl-8 md:pr-4 pt-4 pb-6">
      <button
        type="button"
        onClick={onClose}
        className="inline-flex items-center justify-center rounded-sm bg-[#C5A059] hover:bg-[#b8914f] text-gray-900 font-semibold text-[13px] leading-none shadow-sm transition-colors px-3 py-3.5"
      >
        Close
      </button>
    </footer>
  </div>
);

// ─── Terms Panel ────────────────────────────────────────────────────────────────
const TERMS_LAST_REVISION = 'Last revision: 19 October 2024';

const TermsPanel: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <div className="flex min-h-0 flex-1 flex-col">
    <LegalDocumentPanel
      title="Terms of Use"
      revisionLine={TERMS_LAST_REVISION}
      proseToneClassName="[&_.max-w-none]:!text-gray-300 [&_p]:!text-gray-300 [&_li]:!text-gray-300 [&_h3]:!text-white [&_strong]:!text-gray-100"
      onClose={onClose}
    >
      <TermsContent />
    </LegalDocumentPanel>
  </div>
);

// ─── Privacy Panel ──────────────────────────────────────────────────────────────
const PRIVACY_LAST_REVISION = 'Last updated: November 20, 2024';

const PrivacyPanel: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <div className="flex min-h-0 flex-1 flex-col">
    <LegalDocumentPanel
      title="Privacy Policy"
      revisionLine={PRIVACY_LAST_REVISION}
      proseToneClassName="[&_.max-w-none]:!text-gray-300 [&_p]:!text-gray-300 [&_li]:!text-gray-300 [&_h3]:!text-white [&_ul]:!text-gray-300"
      onClose={onClose}
    >
      <PrivacyContent />
    </LegalDocumentPanel>
  </div>
);

// ─── Pause Profile Panel ────────────────────────────────────────────────────────
const PausePanel: React.FC<{ onDone: () => void }> = ({ onDone }) => (
  <div
    className="flex-1 min-h-0 overflow-y-auto prairi-hide-native-scrollbar"
    style={{ backgroundColor: SETTINGS_SURFACE }}
  >
    <PauseProfilePage onDone={onDone} />
  </div>
);

const DataProfilePanel: React.FC = () => (
  <div
    className="flex-1 min-h-0 overflow-y-auto prairi-hide-native-scrollbar"
    style={{ backgroundColor: PRAIRI_SURFACES.page }}
  >
    <InvestorProfileComponent isOwnProfile adminCompact />
  </div>
);

/** Edit wizard stays inside Settings (same pattern as startup profile edit). */
const EditProfileInSettingsPanel: React.FC<{
  userType: 'investor' | 'startup';
  onDone: () => void;
  leaveGuardRef: React.MutableRefObject<ProfileWizardLeaveGuard | null>;
}> = ({ userType, onDone, leaveGuardRef }) => (
  <div
    className="flex-1 min-h-0 overflow-y-auto scrollbar-visible-dark"
    style={{ backgroundColor: PRAIRI_SURFACES.page }}
  >
    <ProfileWizard userType={userType} embedded onSave={onDone} leaveGuardRef={leaveGuardRef} />
  </div>
);

// ─── Investor Privacy Panel ─────────────────────────────────────────────────────
const InvestorPrivacyPanel: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const fns = getFunctions();
  const [shareIdentity, setShareIdentity] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<{ success: boolean; message: string } | null>(null);

  useEffect(() => {
    if (!user) return;
    setShareIdentity(user.privacyPreferences?.shareIdentityWithStartups === true);
  }, [user]);

  const handleSave = async () => {
    if (!user) return;
    try {
      setSaving(true);
      setSaveResult(null);
      const result = await httpsCallable(
        fns,
        'updateInvestorPrivacyPreferences'
      )({ shareIdentityWithStartups: shareIdentity });
      const data = result.data as { success: boolean; error?: string };
      if (!data.success) {
        throw new Error(data.error || 'Failed to save privacy preferences');
      }
      await refreshUser();
      setSaveResult({ success: true, message: 'Privacy preferences saved.' });
      setTimeout(() => setSaveResult(null), 5000);
    } catch (error: unknown) {
      setSaveResult({
        success: false,
        message: `Failed to save: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="p-6 md:p-8 max-w-lg flex-1 min-h-0 overflow-y-auto text-left prairi-hide-native-scrollbar"
      style={{ backgroundColor: SETTINGS_SURFACE }}
    >
      <h2 className="text-white text-xl font-bold mb-2">Profile visibility</h2>
      <p className="text-gray-400 text-sm mb-6">
        By default, startups only see that an investor from your country viewed their profile — not
        your name. You can opt in to share your identity when browsing.
      </p>

      <label className="flex items-start cursor-pointer mb-6">
        <input
          type="checkbox"
          checked={shareIdentity}
          onChange={e => setShareIdentity(e.target.checked)}
          className="sr-only"
        />
        <div
          className="w-6 h-6 border-2 rounded flex items-center justify-center transition-all mr-3 flex-shrink-0 mt-0.5"
          style={{
            borderColor: '#92d050',
            backgroundColor: shareIdentity ? '#92d050' : 'transparent',
          }}
        >
          {shareIdentity && (
            <svg
              className="w-4 h-4 text-black"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M5 13l4 4L19 7"
              />
            </svg>
          )}
        </div>
        <div>
          <p className="text-white font-medium text-sm">Share my identity with startups I view</p>
          <p className="text-gray-400 text-xs mt-1">
            When off, startups see &ldquo;Investor from [country]&rdquo; on their dashboard.
            Favoriting or messaging a startup may still reveal you depending on your choices there.
          </p>
        </div>
      </label>

      {saveResult && (
        <p className={`text-sm mb-4 ${saveResult.success ? 'text-emerald-400' : 'text-red-400'}`}>
          {saveResult.message}
        </p>
      )}

      <button
        type="button"
        onClick={handleSave}
        disabled={saving}
        className="border-2 border-emerald-500 text-white px-6 py-2 rounded-xl font-bold disabled:opacity-50"
      >
        {saving ? 'Saving...' : 'Save preferences'}
      </button>
    </div>
  );
};

// ─── Success fee (non-US investors) ────────────────────────────────────────────
const SuccessFeePanel: React.FC = () => {
  const { user } = useAuth();
  const { investorProfile, updateProfile } = useProfile();
  const [paysFee, setPaysFee] = useState<boolean | null>(investorProfile?.paysFee ?? null);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<{ success: boolean; message: string } | null>(null);

  useEffect(() => {
    if (investorProfile?.paysFee === undefined) return;
    setPaysFee(investorProfile.paysFee);
  }, [investorProfile?.paysFee]);

  const handleSave = async () => {
    if (!user || paysFee === null) {
      setSaveResult({ success: false, message: 'Select how the success fee is paid.' });
      return;
    }
    if (!investorProfile) {
      setSaveResult({
        success: false,
        message: 'Finish your investor profile before updating the success fee.',
      });
      return;
    }
    try {
      setSaving(true);
      setSaveResult(null);
      await updateProfile(user.id, 'investor', {
        paysFee,
        hasAcceptedFeeAgreement: true,
      });
      setSaveResult({ success: true, message: 'Success fee saved.' });
      setTimeout(() => setSaveResult(null), 5000);
    } catch (error: unknown) {
      setSaveResult({
        success: false,
        message: `Failed to save: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="p-6 md:p-8 flex flex-1 min-h-0 flex-col overflow-y-auto text-left prairi-hide-native-scrollbar"
      style={{ backgroundColor: SETTINGS_SURFACE }}
    >
      <h2 className="text-2xl font-bold text-white uppercase mb-6">Success fee</h2>
      <p className="text-gray-300 mb-6">
        For each investment made into a startup discovered through PRAIRI (unless they&apos;ve
        pitched you in the last 6 months), the 2% success fee will be paid by:
      </p>

      <div className="space-y-6 mb-6">
        {/* Option 1: PRAIRI invoices the investor separately — startup sees the Startup-Friendly badge. */}
        <label className="flex items-start space-x-3 cursor-pointer">
          <input
            type="radio"
            name="settings-success-fee"
            checked={paysFee === true}
            onChange={() => {
              setPaysFee(true);
              setSaveResult(null);
            }}
            className="mt-1 w-5 h-5 text-emerald-500 border-gray-600 focus:ring-emerald-500"
          />
          <div className="min-w-0 flex-1 text-left">
            <span className="text-white text-lg">Separately by us </span>
            <span className="text-emerald-400 text-sm italic">
              (preferred by highly sought-after startups)
            </span>
            <p className="mt-3 text-sm text-gray-400">
              Displayed to the startup when you message them:
            </p>
            <div
              style={{ backgroundColor: PRAIRI_SURFACES.profileCard }}
              className={`mt-2 flex w-full max-w-[21rem] items-center space-x-2.5 rounded-xl border p-3 text-left transition-colors ${
                paysFee === true ? 'border-[#0ea5e9]' : 'border-gray-600/60'
              }`}
            >
              <span className="text-[#92d050] text-base leading-none flex-shrink-0">★</span>
              <div className="min-w-0 flex-1">
                <p className="text-white text-sm font-bold break-words">
                  Startup-Friendly Investor
                </p>
                <p className="mt-0.5 text-xs text-gray-300 leading-snug break-words">
                  This investor has generously agreed to pay PRAIRI&apos;s &apos;success fee&apos;
                  from their own funds.
                </p>
              </div>
            </div>
          </div>
        </label>

        {/* Option 2: success fee comes out of the startup's allocation. */}
        <label className="flex items-start space-x-3 cursor-pointer">
          <input
            type="radio"
            name="settings-success-fee"
            checked={paysFee === false}
            onChange={() => {
              setPaysFee(false);
              setSaveResult(null);
            }}
            className="mt-1 w-5 h-5 text-emerald-500 border-gray-600 focus:ring-emerald-500"
          />
          <div className="min-w-0 flex-1 text-left">
            <span className="text-white text-lg">From the startup&apos;s allocation</span>
            <p className="mt-3 text-sm text-gray-400">
              Displayed to the startup when you message them:
            </p>
            <div
              style={{ backgroundColor: PRAIRI_SURFACES.profileCard }}
              className={`mt-2 flex w-full max-w-[21rem] items-start space-x-2.5 rounded-xl border p-3 text-left transition-colors ${
                paysFee === false ? 'border-[#0ea5e9]' : 'border-gray-600/60'
              }`}
            >
              <span className="text-amber-400 text-xs leading-none mt-1 flex-shrink-0">●</span>
              <p className="text-xs text-gray-300 leading-snug break-words">
                PRAIRI&apos;s &apos;success fee&apos; (2% of this investor&apos;s total investment)
                will be allocated from the amount they provide you.
              </p>
            </div>
          </div>
        </label>
      </div>

      {saveResult && (
        <p className={`text-sm mb-4 ${saveResult.success ? 'text-emerald-400' : 'text-red-400'}`}>
          {saveResult.message}
        </p>
      )}

      <div className="mt-auto flex justify-center pt-10">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || paysFee === null}
          className="border-2 border-emerald-500 text-white px-6 py-2 rounded-xl font-bold disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save success fee'}
        </button>
      </div>
    </div>
  );
};

// ─── Menu definitions ──────────────────────────────────────────────────────────
type InlinePanelId =
  | 'activity'
  | 'notifications'
  | 'password'
  | 'investorPrivacy'
  | 'successFee'
  | 'terms'
  | 'privacy'
  | 'pause'
  | 'dataProfile'
  | 'editDataProfile'
  | 'editProfile';
type MenuItemId = InlinePanelId | 'profile' | 'logout';

interface MenuItem {
  id: MenuItemId;
  label: string;
  investorOnly?: boolean;
  startupOnly?: boolean;
  /** Non-US investors only. US investors do not pay this choice. */
  nonUsInvestorOnly?: boolean;
  /** Hidden on small screens (still shown to investors on md+). */
  hideOnMobile?: boolean;
}

/** Investor menu order: profile items live inside Settings with the rest. */
const INVESTOR_MENU: MenuItem[] = [
  { id: 'dataProfile', label: 'Data Profile', investorOnly: true },
  {
    id: 'editDataProfile',
    label: 'Edit your data profile',
    investorOnly: true,
  },
  { id: 'investorPrivacy', label: 'Profile visibility', investorOnly: true },
  { id: 'notifications', label: 'Notification settings' },
  { id: 'successFee', label: 'Success fee', investorOnly: true, nonUsInvestorOnly: true },
  { id: 'password', label: 'Change your password' },
  { id: 'pause', label: 'Pause profile' },
  { id: 'terms', label: 'Terms & Conditions' },
  { id: 'privacy', label: 'Privacy Policy' },
  { id: 'logout', label: 'Log out' },
];

const STARTUP_MENU: MenuItem[] = [
  { id: 'notifications', label: 'Notification settings' },
  { id: 'password', label: 'Change your password' },
  { id: 'profile', label: 'Profile', startupOnly: true },
  { id: 'editProfile', label: 'Edit profile', startupOnly: true },
  { id: 'pause', label: 'Pause/Reactivate your match profile' },
  { id: 'terms', label: 'Terms & Conditions' },
  { id: 'privacy', label: 'Privacy Policy' },
  { id: 'logout', label: 'Log out' },
];

// ─── Main Page ──────────────────────────────────────────────────────────────────
const SettingsPage: React.FC = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { investorProfile } = useProfile();
  const isInvestor = user?.userType === 'investor';
  const [activePanel, setActivePanel] = useState<InlinePanelId>(
    isInvestor ? 'activity' : 'notifications'
  );
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);
  // On mobile: true = content visible, false = menu visible
  const [mobileShowContent, setMobileShowContent] = useState(false);
  // Same lg (1024) shell as Favorites / profile — md side-by-side crushes ASUS 1080p heights.
  const [isLgUp, setIsLgUp] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches
  );
  const [matchedStartups, setMatchedStartups] = useState<StartupProfile[]>([]);
  const [matchesLoading, setMatchesLoading] = useState(true);
  const editLeaveGuardRef = useRef<ProfileWizardLeaveGuard | null>(null);

  const openPanel = (panel: InlinePanelId, showContent = true) => {
    setActivePanel(panel);
    setMobileShowContent(showContent);
  };

  const handleLegalPanelClose = () => {
    openPanel(isInvestor ? 'activity' : 'notifications', false);
  };

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = () => setIsLgUp(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!isInvestor) return;
    // First paint may be before userType resolves; open Activity by default for investors.
    setActivePanel(prev => (prev === 'notifications' ? 'activity' : prev));
  }, [isInvestor]);

  // Header "Activity & Settings" lands on Activity. "Broaden your preferences"
  // opens the same Edit your data profile panel used from Settings.
  useEffect(() => {
    if (!isInvestor) return;
    const panel = (location.state as { panel?: string } | null)?.panel;
    if (panel === 'activity') {
      setActivePanel('activity');
      setMobileShowContent(false);
      window.history.replaceState({}, document.title);
    } else if (panel === 'editDataProfile') {
      setActivePanel('editDataProfile');
      setMobileShowContent(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state, isInvestor]);

  useEffect(() => {
    if (!isInvestor) {
      setMatchesLoading(false);
      return;
    }
    if (!user?.id) return;
    let cancelled = false;
    setMatchesLoading(true);
    const loadMatches = async () => {
      try {
        const getInvestorMatches = httpsCallable(getFunctions(), 'getInvestorMatches');
        const result = await getInvestorMatches({ userId: user.id });
        const data = result.data as { success?: boolean; matches?: StartupProfile[] };
        if (cancelled) return;
        if (data?.success && Array.isArray(data.matches)) {
          setMatchedStartups(data.matches);
        }
      } catch (err) {
        console.error('Error loading Specially for you on settings:', err);
      } finally {
        if (!cancelled) setMatchesLoading(false);
      }
    };
    loadMatches();
    return () => {
      cancelled = true;
    };
  }, [isInvestor, user?.id]);

  const basedCountry = investorProfile?.country || user?.country || '';
  const menuItems = (isInvestor ? INVESTOR_MENU : STARTUP_MENU).filter(item => {
    if (item.investorOnly && !isInvestor) return false;
    if (item.startupOnly && user?.userType !== 'startup') return false;
    if (item.nonUsInvestorOnly && basedCountry === 'United States') return false;
    if (item.id === 'successFee' && !SUCCESS_FEE_LIVE) return false;
    if (item.hideOnMobile && !isLgUp) return false;
    return true;
  });

  const handleMenuClick = (item: MenuItem) => {
    if (item.id === 'logout') {
      const leavingEdit = activePanel === 'editDataProfile' || activePanel === 'editProfile';
      if (leavingEdit && editLeaveGuardRef.current) {
        editLeaveGuardRef.current.tryLeave(() => setShowLogoutPopup(true));
        return;
      }
      setShowLogoutPopup(true);
      return;
    }
    if (item.id === 'profile') {
      const leavingEdit = activePanel === 'editDataProfile' || activePanel === 'editProfile';
      if (leavingEdit && editLeaveGuardRef.current) {
        editLeaveGuardRef.current.tryLeave(() => navigate('/profile/startup/view'));
        return;
      }
      navigate('/profile/startup/view');
      return;
    }
    const nextPanel = item.id as InlinePanelId;
    const leavingEdit =
      (activePanel === 'editDataProfile' || activePanel === 'editProfile') &&
      nextPanel !== activePanel;
    if (leavingEdit && editLeaveGuardRef.current) {
      editLeaveGuardRef.current.tryLeave(() => openPanel(nextPanel));
      return;
    }
    openPanel(nextPanel);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      setShowLogoutPopup(false);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const renderPanel = () => {
    switch (activePanel) {
      case 'activity':
        return <InvestorActivityPanel />;
      case 'dataProfile':
        return <DataProfilePanel />;
      case 'editDataProfile':
        return (
          <EditProfileInSettingsPanel
            key="edit-investor"
            userType="investor"
            leaveGuardRef={editLeaveGuardRef}
            onDone={() => openPanel('dataProfile')}
          />
        );
      case 'editProfile':
        return (
          <EditProfileInSettingsPanel
            key="edit-startup"
            userType="startup"
            leaveGuardRef={editLeaveGuardRef}
            onDone={() => openPanel('notifications')}
          />
        );
      case 'notifications':
        return <NotificationsPanel />;
      case 'password':
        return <PasswordPanel />;
      case 'investorPrivacy':
        return <InvestorPrivacyPanel />;
      case 'successFee':
        return <SuccessFeePanel />;
      case 'terms':
        return <TermsPanel onClose={handleLegalPanelClose} />;
      case 'privacy':
        return <PrivacyPanel onClose={handleLegalPanelClose} />;
      case 'pause':
        return <PausePanel onDone={handleLegalPanelClose} />;
    }
  };

  const isInlinePanel = (id: MenuItemId): id is InlinePanelId =>
    id !== 'profile' && id !== 'logout';

  const activitySelected = isInvestor && activePanel === 'activity';
  /** Settings card is the selected card unless the investor is on Activity. */
  const settingsSelected = !isInvestor || activePanel !== 'activity';
  // Investor height comes from the Activity card plus Settings. Startups have no
  // Activity card, so the Settings card fills that same column and the divider stays low.
  const settingsShellHeight = isInvestor
    ? SETTINGS_CARD_HEIGHT
    : `${SETTINGS_CARD_HEIGHT} lg:h-[calc(100vh-5.75rem)]`;

  return (
    <div
      className="min-h-screen flex flex-col pt-0 overflow-visible bg-[#1d1d1d]"
      style={{ minHeight: 'calc(100vh - 80px)', backgroundColor: PAGE_BG }}
    >
      {/* Same shell as Favorites: 28% nav + activity, then divider + SFY */}
      <div className="relative w-full px-3 pt-0 pb-0 lg:px-4">
        <div className="w-full">
          <div className="flex w-full flex-col gap-4 lg:flex-row lg:items-stretch lg:gap-0">
            <div
              className={`relative flex min-h-0 w-full flex-col overflow-x-hidden lg:w-[28%] lg:shrink-0 ${settingsShellHeight} ${
                mobileShowContent ? 'hidden lg:flex' : ''
              }`}
            >
              {isInvestor && (
                <div className="pointer-events-none relative z-20 flex flex-shrink-0 justify-center -mb-4">
                  <div
                    className="w-fit min-w-[9.5rem] rounded-b-2xl border-x border-b border-gray-800 bg-[#151515] px-12 py-3.5 text-center text-sm !font-normal leading-snug text-white shadow-[0_8px_20px_rgba(0,0,0,0.22)]"
                    style={{ fontFamily: 'Rubik, sans-serif', fontWeight: 400 }}
                  >
                    Activity & Settings
                  </div>
                </div>
              )}
              <div
                className={`relative flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto prairi-hide-native-scrollbar px-3 pb-3 ${
                  isInvestor ? 'pt-1' : 'pt-6'
                }`}
              >
                {isInvestor && (
                  <button
                    type="button"
                    onClick={() => {
                      const leavingEdit =
                        activePanel === 'editDataProfile' || activePanel === 'editProfile';
                      if (leavingEdit && editLeaveGuardRef.current) {
                        editLeaveGuardRef.current.tryLeave(() => openPanel('activity'));
                        return;
                      }
                      openPanel('activity');
                    }}
                    className={`relative z-10 mb-5 flex min-h-[7.5rem] w-full shrink-0 flex-col items-center justify-center rounded-3xl px-3 pb-5 pt-7 transition-colors ${
                      activitySelected ? 'bg-[#3a3a3a]' : 'hover:bg-[#383838]'
                    }`}
                    style={{
                      backgroundColor: activitySelected ? '#3a3a3a' : SETTINGS_NAV_SURFACE,
                      border: activitySelected
                        ? `1px solid ${SETTINGS_CARD_BORDER}`
                        : '1px solid transparent',
                    }}
                  >
                    <img
                      className="h-14 w-14 object-contain"
                      src={activitySelected ? activityIconSelected : activityIconNotSelected}
                      alt=""
                      aria-hidden
                    />
                    <span
                      className={`mt-2 text-base ${
                        activitySelected ? 'font-bold text-white' : 'font-normal text-[#c8c8c8]'
                      }`}
                    >
                      Activity
                    </span>
                  </button>
                )}

                <nav
                  className={`relative flex w-full flex-col rounded-3xl ${
                    isInvestor ? 'shrink-0' : 'min-h-0 flex-1'
                  }`}
                  style={{
                    backgroundColor: SETTINGS_NAV_SURFACE,
                    border: settingsSelected
                      ? `1px solid ${SETTINGS_CARD_BORDER}`
                      : '1px solid transparent',
                  }}
                >
                  <div className="flex flex-col items-center px-3 pb-2 pt-5">
                    <img
                      className="h-14 w-14 object-contain"
                      src={
                        !isInvestor
                          ? settingsIconHalf
                          : settingsSelected
                            ? settingsIconSelected
                            : settingsIconInactive
                      }
                      alt=""
                      aria-hidden
                    />
                    <span
                      className={`mt-2 text-sm ${
                        settingsSelected ? 'font-bold text-white' : 'font-normal text-[#c8c8c8]'
                      }`}
                    >
                      Settings
                    </span>
                  </div>

                  <ul className="px-2 pb-6 pt-1.5">
                    {menuItems.map(item => {
                      const isActive =
                        settingsSelected && isInlinePanel(item.id) && activePanel === item.id;

                      return (
                        <li key={item.id}>
                          <button
                            type="button"
                            onClick={() => handleMenuClick(item)}
                            className={`
                    w-full px-2 py-[0.45rem] text-center text-[0.9375rem] leading-snug transition-colors
                    ${
                      isActive
                        ? 'font-bold text-white no-underline'
                        : 'font-normal text-[#c8c8c8] underline decoration-[#c8c8c8] decoration-1 underline-offset-[3px] hover:text-white hover:decoration-white'
                    }
                  `}
                          >
                            {item.label}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </nav>
              </div>
            </div>

            <div
              className={`
                flex min-h-0 min-w-0 flex-1 flex-col
                ${settingsShellHeight}
                lg:border-l-[0.75rem] lg:border-black
                lg:-mr-4
                ${mobileShowContent ? 'flex' : 'hidden lg:flex lg:flex-col'}
              `}
              style={{ backgroundColor: SETTINGS_SURFACE }}
            >
              <button
                className="shrink-0 lg:hidden flex items-center gap-2 text-gray-400 hover:text-white text-sm px-4 pt-4 pb-2 w-fit"
                onClick={() => setMobileShowContent(false)}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                Back
              </button>

              <div
                className="flex flex-1 flex-col min-h-0 overflow-hidden min-w-0"
                style={{ backgroundColor: SETTINGS_SURFACE }}
              >
                {renderPanel()}
              </div>
            </div>
          </div>
        </div>
        <div className="h-3 bg-black -mx-3 lg:-mx-4" aria-hidden="true" />
      </div>

      {isInvestor && !matchesLoading && (
        <div className="relative z-10 mb-10 overflow-visible pl-3 pt-14 sm:pl-4 lg:pl-6">
          <StartupRow
            title={CATALOG_CATEGORY_LABELS.speciallyForYou}
            startups={matchedStartups}
            onSelectStartup={startup => {
              const userId = startup.userId || startup.id;
              const slug = startupNameSlug(startup.companyName);
              if (userId) rememberOpenStartupProfile({ slug, userId: String(userId) });
              navigate(`/dashboard/${slug}`);
            }}
          />
        </div>
      )}

      {/* ── Logout Confirmation Popup ─────────────────────────────────── */}
      {showLogoutPopup && (
        <div
          className="fixed inset-0 z-[100000] bg-black bg-opacity-60 flex items-center justify-center"
          onClick={() => setShowLogoutPopup(false)}
        >
          <div
            className="bg-gray-800 rounded-xl p-6 max-w-sm mx-4 shadow-xl"
            onClick={e => e.stopPropagation()}
          >
            <h2 className="text-white text-xl font-bold mb-3">Confirm Logout</h2>
            <p className="text-gray-300 mb-6">Are you sure you want to log out?</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowLogoutPopup(false)}
                className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
              >
                Stay
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors"
              >
                Log out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
